import type { OpportunityFeedFilters, OpportunityFeedItem, OpportunityState } from './types'
import { Button, Card } from '@heroui/react'
import { useQuery } from '@tanstack/react-query'
import { ExternalLink, PanelRightClose, ShieldCheck } from 'lucide-react'
import * as React from 'react'
import { useTranslations } from 'use-intl'

import { opportunitiesQueryOptions, opportunityDetailQueryOptions } from './query-options'

const states: OpportunityState[] = ['detected', 'visible', 'cooling_down', 'expired', 'closed']
const opportunityTypes = ['prediction_market_whale_trade'] as const

function verifierLabel(state: OpportunityFeedItem['verifierState'], t: ReturnType<typeof useTranslations<'OpportunityMonitoring'>>) {
  switch (state) {
    case 'verified_arb':
      return t('verifier.verifiedArb')
    case 'verified_no_arb':
      return t('verifier.verifiedNoArb')
    case 'unverified':
      return t('verifier.unverified')
    case 'pending':
      return t('verifier.pending')
    default:
      return t('verifier.none')
  }
}

function verifierClass(state: OpportunityFeedItem['verifierState']) {
  switch (state) {
    case 'verified_arb':
      return 'bg-emerald-100 text-emerald-800'
    case 'verified_no_arb':
      return 'bg-sky-100 text-sky-800'
    case 'unverified':
      return 'bg-stone-200 text-stone-700'
    case 'pending':
      return 'bg-amber-100 text-amber-800'
    default:
      return 'bg-stone-100 text-stone-600'
  }
}

function formatTime(value: string | null) {
  return value ? new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'n/a'
}

export function FreshnessStatus({
  dataUpdatedAt,
  isFetching,
  isError,
  isStale,
}: {
  dataUpdatedAt: number
  isFetching: boolean
  isError: boolean
  isStale: boolean
}) {
  const t = useTranslations('OpportunityMonitoring')
  const hasData = dataUpdatedAt > 0
  const updatedLabel = hasData
    ? new Date(dataUpdatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : t('freshness.notRefreshed')
  const status = isFetching && hasData
    ? t('freshness.refreshing')
    : isError
      ? t('freshness.failed')
      : isStale && hasData
        ? t('freshness.stale')
        : hasData
          ? t('freshness.fresh')
          : t('freshness.loading')

  return (
    <div className="flex flex-wrap items-center gap-2 text-sm text-muted" aria-live="polite">
      <span>{t('freshness.lastRefreshed', { time: updatedLabel })}</span>
      <span className="rounded-md border border-divider px-2 py-1 font-semibold">{status}</span>
    </div>
  )
}

function Filters({
  filters,
  onChange,
}: {
  filters: OpportunityFeedFilters
  onChange: (filters: OpportunityFeedFilters) => void
}) {
  const t = useTranslations('OpportunityMonitoring')
  return (
    <div className="flex flex-wrap items-center gap-2">
      <select
        aria-label={t('filters.source')}
        className="rounded-md border border-divider bg-background px-3 py-2 text-sm"
        value={filters.sourceType ?? ''}
        onChange={event => onChange({ ...filters, sourceType: event.target.value ? event.target.value as OpportunityFeedFilters['sourceType'] : undefined })}
      >
        <option value="">{t('filters.allSources')}</option>
        <option value="polymarket_trade">Polymarket</option>
        <option value="sports_verifier">Sports Verifier</option>
        <option value="crypto_signal">Crypto Signal</option>
      </select>
      <select
        aria-label={t('filters.status')}
        className="rounded-md border border-divider bg-background px-3 py-2 text-sm"
        value={filters.state ?? ''}
        onChange={event => onChange({ ...filters, state: event.target.value ? event.target.value as OpportunityState : undefined })}
      >
        <option value="">{t('filters.allStatuses')}</option>
        {states.map(state => <option key={state} value={state}>{t(`states.${state}`)}</option>)}
      </select>
      <select
        aria-label={t('filters.opportunityType')}
        className="rounded-md border border-divider bg-background px-3 py-2 text-sm"
        value={filters.opportunityType ?? ''}
        onChange={event => onChange({ ...filters, opportunityType: event.target.value || undefined })}
      >
        <option value="">{t('filters.allTypes')}</option>
        {opportunityTypes.map(type => <option key={type} value={type}>{t(`types.${type}`)}</option>)}
      </select>
      <label className="flex items-center gap-2 rounded-md border border-divider px-3 py-2 text-sm">
        <input
          type="checkbox"
          checked={Boolean(filters.verifiedOnly)}
          onChange={event => onChange({ ...filters, verifiedOnly: event.target.checked })}
        />
        {t('filters.verifiedOnly')}
      </label>
    </div>
  )
}

function FeedRow({
  item,
  selected,
  onSelect,
}: {
  item: OpportunityFeedItem
  selected: boolean
  onSelect: () => void
}) {
  const t = useTranslations('OpportunityMonitoring')
  return (
    <button
      type="button"
      className={`grid w-full gap-3 border-b border-divider p-4 text-left transition hover:bg-content2 md:grid-cols-[1fr_150px_130px_90px] ${selected ? 'bg-content2' : ''}`}
      onClick={onSelect}
    >
      <div>
        <div className="font-semibold">{item.title}</div>
        <div className="mt-1 text-sm text-muted">
          {item.outcomeLabel}
          {' '}
          ·
          {' '}
          {item.opportunityType}
        </div>
      </div>
      <div className="text-sm">
        <span className={`rounded-full px-2 py-1 text-xs font-semibold ${verifierClass(item.verifierState)}`}>
          {verifierLabel(item.verifierState, t)}
        </span>
      </div>
      <div className="text-sm">
        <div className="font-semibold">
          {item.currentEdgeBps ?? 0}
          {' '}
          bps
        </div>
        <div className="text-muted">{t('feed.rank', { score: item.rankScoreBps })}</div>
      </div>
      <div className="text-sm text-muted">{formatTime(item.lastDecisionAt)}</div>
    </button>
  )
}

function DetailPanel({ opportunityId, onClose }: { opportunityId: string | null, onClose: () => void }) {
  const t = useTranslations('OpportunityMonitoring')
  const detailQuery = useQuery({
    ...opportunityDetailQueryOptions(opportunityId ?? ''),
    enabled: Boolean(opportunityId),
  })

  if (!opportunityId) {
    return (
      <aside className="hidden border-l border-divider p-5 xl:block">
        <div className="text-sm text-muted">{t('detail.empty')}</div>
      </aside>
    )
  }

  if (detailQuery.isPending) {
    return <aside className="border-l border-divider p-5">{t('detail.loading')}</aside>
  }

  if (detailQuery.isError) {
    return <aside className="border-l border-divider p-5" role="alert">{t('detail.error')}</aside>
  }

  const detail = detailQuery.data

  return (
    <aside className="border-l border-divider bg-background p-5 xl:min-w-[380px]">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-black">{detail.title}</h2>
          <p className="mt-1 text-sm text-muted">
            {detail.outcomeLabel}
            {' '}
            ·
            {' '}
            {detail.state}
          </p>
        </div>
        <Button size="sm" variant="ghost" isIconOnly aria-label={t('detail.close')} onPress={onClose}>
          <PanelRightClose className="size-4" />
        </Button>
      </div>

      <div className="mt-5 grid gap-3 text-sm">
        <div className="rounded-md border border-divider p-3">
          <div className="text-muted">{t('detail.verifier')}</div>
          <div className="mt-1 flex items-center gap-2 font-semibold">
            <ShieldCheck className="size-4" />
            {verifierLabel(detail.verifierState, t)}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-md border border-divider p-3">
            <div className="text-muted">{t('detail.currentEdge')}</div>
            <div className="mt-1 font-semibold">
              {detail.currentEdgeBps ?? 0}
              {' '}
              bps
            </div>
          </div>
          <div className="rounded-md border border-divider p-3">
            <div className="text-muted">{t('detail.visibleSince')}</div>
            <div className="mt-1 font-semibold">{formatTime(detail.firstVisibleAt)}</div>
          </div>
        </div>
        <div className="rounded-md border border-divider p-3">
          <div className="text-muted">{t('detail.round')}</div>
          <div className="mt-1 font-semibold">{detail.currentRound?.opportunityRoundId ?? t('detail.noActiveRound')}</div>
        </div>
      </div>

      <div className="mt-5 space-y-2">
        {detail.sourceLinks.map(link => (
          <a
            key={link.href}
            href={link.href}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-md border border-divider px-3 py-2 text-sm font-semibold"
          >
            {link.label}
            <ExternalLink className="size-4" />
          </a>
        ))}
      </div>
    </aside>
  )
}

export function OpportunityMonitoringConsole() {
  const t = useTranslations('OpportunityMonitoring')
  const [filters, setFilters] = React.useState<OpportunityFeedFilters>({ limit: 50 })
  const [selectedId, setSelectedId] = React.useState<string | null>(null)
  const feedQuery = useQuery(opportunitiesQueryOptions(filters))
  const items = feedQuery.data ?? []

  React.useEffect(() => {
    if (!selectedId && items[0]) {
      setSelectedId(items[0].opportunityId)
    }
    if (selectedId && !items.some(item => item.opportunityId === selectedId)) {
      setSelectedId(items[0]?.opportunityId ?? null)
    }
  }, [items, selectedId])

  return (
    <div className="min-h-screen bg-background">
      <div className="grid min-h-screen xl:grid-cols-[1fr_420px]">
        <main className="p-4 md:p-6">
          <header className="mb-5 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <h1 className="text-3xl font-black tracking-tight">{t('title')}</h1>
              <p className="mt-1 text-sm text-muted">{t('subtitle')}</p>
            </div>
            <div className="flex flex-col gap-3 md:items-end">
              <FreshnessStatus
                dataUpdatedAt={feedQuery.dataUpdatedAt}
                isFetching={feedQuery.isFetching}
                isError={feedQuery.isError}
                isStale={feedQuery.isStale}
              />
              <Filters filters={filters} onChange={setFilters} />
            </div>
          </header>

          <Card className="overflow-hidden" variant="secondary">
            {feedQuery.isPending && <div className="p-6 text-sm text-muted">{t('feed.loading')}</div>}
            {feedQuery.isError && (
              <div className="p-6 text-sm text-danger" role="alert">
                {items.length > 0 ? t('feed.refreshError') : t('feed.error')}
              </div>
            )}
            {!feedQuery.isPending && !feedQuery.isError && items.length === 0 && (
              <div className="p-6 text-sm text-muted">{t('feed.empty')}</div>
            )}
            {items.map(item => (
              <FeedRow
                key={item.opportunityId}
                item={item}
                selected={item.opportunityId === selectedId}
                onSelect={() => setSelectedId(item.opportunityId)}
              />
            ))}
          </Card>

          {selectedId && (
            <div className="mt-4 xl:hidden">
              <DetailPanel opportunityId={selectedId} onClose={() => setSelectedId(null)} />
            </div>
          )}
        </main>
        <div className="hidden xl:block">
          <DetailPanel opportunityId={selectedId} onClose={() => setSelectedId(null)} />
        </div>
      </div>
    </div>
  )
}
